import React, { useState } from 'react';
import injectSheet from 'react-jss/lib/injectSheet';
import PropTypes from 'prop-types';
import cn from 'classnames';

import FavouriteButton from './FavouriteButton';
import theme from '../../../styles/theme';
import { aliceLikeButtonHoverStyle, aliceLikeButtonStyle } from '../styleButton';
import ChevronRight from '../../../icons/ChevronRight';
import { sponsoredBrand } from '../propTypes';
import stylePlaceholders from '../../../styles/placeholders';

//  Hero image is expected to be 1160x400
const HERO_ASPECT_RATIO = 2.9;
const LIGHT_TEXT_COLOR_SCHEMES = ['#8c5462', '#4e787d', '#4f6aa9', '#908284'];
const getTextColorByBrandColorScheme = colorScheme => (
  LIGHT_TEXT_COLOR_SCHEMES.includes(colorScheme) ? theme.colors.white : theme.colors.black
);

const SponsoredBrandBanner = props => {
  const {
    classes, isGuest, brand, onCtaClick, onFavouriteTrack, onFavouriteChange, isLoading,
  } = props;
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  const hasBackgroundColor = !!brand?.colorScheme;
  const heroSource = brand?.mediaAssets?.[0]?.imageUrl;
  const brandInfoTextColor = getTextColorByBrandColorScheme(brand?.colorScheme);

  if (isLoading) {
    return (
      <div className={cn(classes.placeholder, classes.wrapper)}>
        <div className={classes.loading} />
      </div>
    );
  }

  if (!brand) {
    return null;
  }

  return (
    <article className={classes.wrapper}>
      <div
        className={classes.mainWrapper}
        style={{ backgroundColor: brand?.colorScheme }}
        data-has-background-color={hasBackgroundColor}
      >
        <div
          className={classes.imageWrapper}
        >
          {!isImageLoaded && <div className={classes.skeleton} />}
          <img
            className={isImageLoaded ? classes.imageLoaded : classes.imageLoading}
            src={heroSource}
            alt={brand?.headline || brand?.brandName || 'Brand image'}
            onLoad={() => setIsImageLoaded(true)}
          />
        </div>
        <div className={classes.brandWrapper} style={{ color: brandInfoTextColor }}>
          <div className={classes.brandInfo}>
            <div className={classes.brandName}>{brand?.brandName}</div>
            <div className={classes.headline}>{brand?.headline}</div>
            <div className={classes.description}>{brand?.description}</div>
          </div>
          <div className={classes.buttonsWrapper}>
            <a
              className={classes.button}
              href={brand?.ctaLinkUrl || '#'}
              onClick={onCtaClick}
            >
              <p className={classes.buttonText}>{brand?.ctaText}</p>
              <ChevronRight />
            </a>
            <FavouriteButton
              isFavourited={brand?.status}
              isLoading={brand?.busy}
              isAuthenticated={!isGuest}
              onTrackClick={onFavouriteTrack}
              onClick={onFavouriteChange}
            />
          </div>
        </div>
      </div>
    </article>
  );
};

SponsoredBrandBanner.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  isGuest: PropTypes.bool,
  brand: sponsoredBrand,
  onCtaClick: PropTypes.func,
  onFavouriteTrack: PropTypes.func,
  onFavouriteChange: PropTypes.func,
  isLoading: PropTypes.bool,
};

SponsoredBrandBanner.defaultProps = {
  isGuest: true,
  brand: null,
  onCtaClick: null,
  onFavouriteTrack: null,
  onFavouriteChange: null,
  isLoading: false,
};

/**
 * @type {Object.<string, React.CSSProperties | Object.<string, React.CSSProperties>>}
 */
const styles = {
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '12px',

    [theme.breakpoints.up('md')]: {
      rowGap: '20px',
    },
  },
  mainWrapper: {
    borderRadius: '8px',
    overflow: 'hidden',

    [theme.breakpoints.up('md')]: {
      rowGap: '20px',
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      columnGap: '16px',
      borderRadius: '0px',
    },

    '&[data-has-background-color="true"]': {
      [theme.breakpoints.up('md')]: {
        gap: '0',
      },
    },
  },
  imageWrapper: {
    width: '100%',
    borderRadius: '8px',
    overflow: 'hidden',
    aspectRatio: HERO_ASPECT_RATIO,
    boxSizing: 'border-box',
    position: 'relative',

    '& img': {
      objectFit: 'cover',
      width: '100%',
      height: '100%',
      aspectRatio: HERO_ASPECT_RATIO,
      transition: 'opacity 0.3s ease-in-out',
    },

    [theme.breakpoints.up('md')]: {
      borderRadius: '0px',
    },

    '$mainWrapper[data-has-background-color="true"] &': {
      borderRadius: '0px',

      [theme.breakpoints.up('md')]: {
        aspectRatio: 'unset',
        padding: '30px',
      },
    },
  },
  brandWrapper: {
    padding: '30px 16px',
    display: 'flex',
    flexDirection: 'column',
    rowGap: '16px',

    [theme.breakpoints.up('md')]: {
      justifyContent: 'center',
      padding: '30px',
      justifyItems: 'center',
    },
  },
  brandInfo: {
    fontFamily: theme.fonts.body,
  },
  brandName: {
    fontWeight: 600,
    fontSize: 16,
    lineHeight: 1.5,
  },
  headline: {
    fontWeight: 700,
    fontSize: 36,
    lineHeight: 1.1,
    letterSpacing: '-1px',
  },
  description: {
    fontWeight: 400,
    fontSize: 16,
    lineHeight: 2,
    letterSpacing: '0.35px',
  },
  buttonsWrapper: {
    display: 'flex',
    columnGap: '8px',
  },
  buttonText: {
    textDecoration: 'none',
    fontFamily: theme.fonts.body,
  },
  button: {
    ...aliceLikeButtonStyle,
    ...aliceLikeButtonHoverStyle,

    '& svg': {
      width: 7,
      height: 12,
      flexShrink: 0,
    },
  },
  imageLoading: {
    opacity: 0,
  },
  imageLoaded: {
    opacity: 1,
  },
  skeleton: {
    width: '100%',
    height: '100%',
    backgroundColor: '#e0e0e0',
    animation: '$pulse 1.5s ease-in-out infinite',
  },
  '@keyframes pulse': {
    '0%': {
      opacity: 1,
    },
    '50%': {
      opacity: 0.6,
    },
    '100%': {
      opacity: 1,
    },
  },
  ...stylePlaceholders({
    loading: {
      width: '100%',
    },
  }),
  loading: {
    height: 372,

    [theme.breakpoints.up('md')]: {
      height: 220,
    },
  },
};

export default injectSheet(styles)(SponsoredBrandBanner);
